import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './RetailIntelligenceConsolePage.css';

const ENV_API_BASE = process.env.REACT_APP_RETAIL_INTELLIGENCE_API_URL || '';
const LOCAL_API_BASE = 'http://localhost:8220';
const PROXY_API_BASE = '/retail-intelligence-api';
const DEFAULT_API_BASE = ENV_API_BASE || (isBrowserLocalhost() ? LOCAL_API_BASE : PROXY_API_BASE);
const STORAGE_KEY = 'retail-intelligence-console-api-base';
const API_KEY_HEADER = 'X-Retail-Intelligence-API-Key';
const SAMSAM_COOKBOOK_URL =
  'https://www.foodsafetykorea.go.kr/portal/cookrcp/cookRcpBook.do?menu_no=5108&menu_grp=MENU_NEW03';
const SERVICE_MODE_RETAIL_LINKED = 'RETAIL_LINKED';
const SERVICE_MODE_AIDRQ_STANDALONE = 'AIDRQ_STANDALONE';
const SERVICE_MODE_COPY = {
  [SERVICE_MODE_RETAIL_LINKED]: {
    label: 'GS 연계',
    requestLabel: '구매 가능 SKU 추천',
    resultLabel: 'GS SKU/재고 기반 구매 가능 추천',
    description: 'GS 상품, SKU, 점포, 재고 조건을 함께 확인합니다.',
    productIdLabel: 'GS 대표상품',
  },
  [SERVICE_MODE_AIDRQ_STANDALONE]: {
    label: 'AI닥터큐 단독',
    requestLabel: '건강 장보기 참고 추천',
    resultLabel: '대표상품 기준 건강 장보기 참고 추천',
    description: 'GS SKU와 점포 재고 없이 대표상품 기준으로 추천합니다.',
    productIdLabel: '대표상품',
  },
};

const FALLBACK_SCOPE_USERS = [
  {
    user_no: '1',
    display_name: '사용자 1',
    patients: [
      { patient_id: '101', display_name: '가족 구성원 101', wellness_signal_count: 2 },
      { patient_id: '102', display_name: '가족 구성원 102', wellness_signal_count: 3 },
    ],
    default_patient_ids: ['101', '102'],
  },
  {
    user_no: '2',
    display_name: '사용자 2',
    patients: [{ patient_id: '201', display_name: '가족 구성원 201', wellness_signal_count: 1 }],
    default_patient_ids: ['201'],
  },
];

function readInitialApiBase() {
  try {
    const storedApiBase = window.localStorage.getItem(STORAGE_KEY);
    if (shouldUseStoredApiBase(storedApiBase)) {
      return storedApiBase;
    }
    if (storedApiBase) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    return DEFAULT_API_BASE;
  } catch {
    return DEFAULT_API_BASE;
  }
}

function normalizeBaseUrl(value) {
  return String(value || '').trim().replace(/\/$/, '');
}

function isBrowserLocalhost() {
  if (typeof window === 'undefined') {
    return false;
  }
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function isLocalhostApiBase(value) {
  const normalizedValue = normalizeBaseUrl(value);
  if (!normalizedValue) {
    return false;
  }
  try {
    const parsedUrl = new URL(normalizedValue, window.location.origin);
    return ['localhost', '127.0.0.1', '::1'].includes(parsedUrl.hostname);
  } catch {
    return /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(?::|\/|$)/i.test(normalizedValue);
  }
}

function shouldUseStoredApiBase(value) {
  const normalizedValue = normalizeBaseUrl(value);
  if (!normalizedValue) {
    return false;
  }
  return isBrowserLocalhost() || !isLocalhostApiBase(normalizedValue);
}

function rememberApiBase(value) {
  try {
    const normalizedValue = normalizeBaseUrl(value);
    if (shouldUseStoredApiBase(normalizedValue)) {
      window.localStorage.setItem(STORAGE_KEY, normalizedValue);
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage is optional in embedded test environments.
  }
}

function withApiKeyHeader(options, apiKey) {
  const normalizedApiKey = String(apiKey || '').trim();
  if (!normalizedApiKey) {
    return options;
  }
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      [API_KEY_HEADER]: normalizedApiKey,
    },
  };
}

async function requestJson(apiBase, path, options = {}, apiKey = '') {
  const endpoint = `${normalizeBaseUrl(apiBase)}${path}`;
  let response;
  try {
    response = await fetch(endpoint, withApiKeyHeader(options, apiKey));
  } catch (err) {
    const error = new Error(`API 서버에 연결할 수 없습니다. ${normalizeBaseUrl(apiBase)} 실행 상태를 확인하세요.`);
    error.cause = err;
    throw error;
  }

  const raw = await response.text();
  let body = {};
  try {
    body = raw ? JSON.parse(raw) : {};
  } catch {
    body = { raw };
  }
  if (!response.ok) {
    const error = new Error(body?.detail?.message || body?.detail || raw || `HTTP ${response.status}`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

async function fetchPatientScopes(apiBase, apiKey) {
  return requestJson(apiBase, '/v1/retail/patient-scopes', {}, apiKey);
}

async function postRecommendation(apiBase, payload, apiKey) {
  return requestJson(apiBase, '/v1/retail/recommendations', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }, apiKey);
}

async function postConversationalRecommendation(apiBase, payload, apiKey) {
  return requestJson(apiBase, '/v1/retail/recommendations/conversational', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  }, apiKey);
}

async function fetchRecommendationTrace(apiBase, recommendationSetId, apiKey) {
  if (!recommendationSetId) {
    return null;
  }
  return requestJson(apiBase, `/v1/retail/recommendations/${recommendationSetId}/trace`, {}, apiKey);
}

async function requestLlmExplanation(apiBase, recommendationSetId, apiKey, forceRefresh = false) {
  if (!recommendationSetId) {
    return null;
  }
  return requestJson(
    apiBase,
    `/v1/retail/recommendations/${recommendationSetId}/llm-explanation`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ force_refresh: Boolean(forceRefresh) }),
    },
    apiKey,
  );
}

async function fetchLlmExplanation(apiBase, recommendationSetId, apiKey) {
  if (!recommendationSetId) {
    return null;
  }
  return requestJson(apiBase, `/v1/retail/recommendations/${recommendationSetId}/llm-explanation`, {}, apiKey);
}

function formatJson(value) {
  return JSON.stringify(value, null, 2);
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  try {
    return new Date(value).toLocaleString('ko-KR', { hour12: false });
  } catch {
    return String(value);
  }
}

function hasKoreanFinalConsonant(value) {
  const text = String(value || '').trim();
  if (!text) {
    return false;
  }
  const lastCharCode = text.charCodeAt(text.length - 1);
  if (lastCharCode < 0xac00 || lastCharCode > 0xd7a3) {
    return false;
  }
  return (lastCharCode - 0xac00) % 28 > 0;
}

function withKoreanTopicParticle(value) {
  const text = String(value || '').trim();
  if (!text) {
    return text;
  }
  return `${text}${hasKoreanFinalConsonant(text) ? '은' : '는'}`;
}

function formatMealCardSummary(card) {
  const title = String(card?.title || '').trim();
  const summary = String(card?.summary || '').trim();
  if (!title) {
    return summary;
  }
  if (!summary || summary.includes('삼삼한 밥상 자료에서 추출한 저염 식사 참고 메뉴')) {
    return `${withKoreanTopicParticle(title)} 삼삼한 밥상 자료에서 추출한 저염 식사 참고 메뉴입니다.`;
  }
  return summary;
}

function cleanMealUsageTip(value) {
  let text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) {
    return '';
  }
  text = text
    .replace(/^(?:(?:열량|탄수화물|단백질|지방|나트륨)\s*(?:\([^)]+\))?\s*)+/i, '')
    .trim();
  text = text.replace(/^(?:\d+(?:\.\d+)?\s*){2,}/, '').trim();
  text = text.replace(/\s+\d+(?:\.\d+)?\s*$/, '').trim();
  return text;
}

function isSamsamSource(card) {
  const sourceType = String(card?.source_type || '').toUpperCase();
  const sourceName = String(card?.source_name || '');
  return sourceType.includes('MFDS_SAMSAM') || sourceName.includes('삼삼한 밥상');
}

function formatSourceVolume(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  if (raw.endsWith('권')) {
    return raw;
  }
  const match = raw.match(/\d+/);
  return match ? `${Number(match[0])}권` : raw;
}

function formatSourcePage(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }
  if (raw.endsWith('페이지')) {
    return raw;
  }
  const normalized = raw.replace(/\s+/g, '').toUpperCase();
  if (/^\d+-\d+$/.test(normalized) || /^\d+-[A-Z]$/.test(normalized)) {
    return `${normalized}페이지`;
  }
  const match = normalized.match(/^\d+$/);
  return match ? `${Number(match[0])}페이지` : raw;
}

function formatMealSourceLabel(card) {
  const sourceName = isSamsamSource(card) ? '식약처 삼삼한 밥상' : card?.source_name || card?.source_type || '출처';
  return [sourceName, formatSourceVolume(card?.source_volume), formatSourcePage(card?.source_page)]
    .filter(Boolean)
    .join(' ');
}

function formatMealSourceUrl(card) {
  return card?.source_url || (isSamsamSource(card) ? SAMSAM_COOKBOOK_URL : '');
}

function hasForbiddenResponseFields(response) {
  const forbiddenKeys = new Set(['cart', 'coupon', 'order', 'payment', 'pos', 'raw_report', 'clinical_notes', 'internal_score']);
  const stack = [response];
  while (stack.length) {
    const current = stack.pop();
    if (!current || typeof current !== 'object') {
      continue;
    }
    if (Array.isArray(current)) {
      current.forEach((item) => stack.push(item));
      continue;
    }
    for (const [key, value] of Object.entries(current)) {
      if (forbiddenKeys.has(String(key).toLowerCase())) {
        return true;
      }
      stack.push(value);
    }
  }
  return false;
}

function normalizeScopeUsers(users) {
  if (!Array.isArray(users)) {
    return [];
  }
  return users
    .map((user) => {
      const patients = Array.isArray(user.patients)
        ? user.patients.filter((patient) => patient?.patient_id).map((patient) => ({
            patient_id: String(patient.patient_id),
            display_name: patient.display_name || `가족 구성원 ${patient.patient_id}`,
            wellness_signal_count: Number(patient.wellness_signal_count || 0),
          }))
        : [];
      return {
        user_no: String(user.user_no || ''),
        display_name: user.display_name || `사용자 ${user.user_no}`,
        patients,
        default_patient_ids: Array.isArray(user.default_patient_ids)
          ? user.default_patient_ids.map(String)
          : patients.map((patient) => patient.patient_id),
      };
    })
    .filter((user) => user.user_no && user.patients.length > 0);
}

function selectedPatientIdsForUser(user) {
  return user?.default_patient_ids?.length ? user.default_patient_ids : user?.patients?.map((patient) => patient.patient_id) || [];
}

function formatTraceTag(tag) {
  return tag?.tag_code || tag?.code || tag?.signal || '-';
}

function formatTraceNumber(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(3);
  }
  return String(value);
}

function formatRecommendationMode(mode) {
  if (mode === 'HEALTH_MAINTENANCE') {
    return '건강 유지형 균형 장보기';
  }
  return '건강목표 기반 추천';
}

function serviceModeCopy(serviceMode) {
  return SERVICE_MODE_COPY[serviceMode] || SERVICE_MODE_COPY[SERVICE_MODE_RETAIL_LINKED];
}

function formatRationaleCode(code) {
  const labels = {
    family_priority_compressed: '가족 공통 우선순위',
    health_maintenance_default: '건강 유지 기본값',
  };
  return labels[code] || code || '-';
}

function formatTraceSources(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return '-';
  }
  return values.join(', ');
}

function formatNutritionValue(nutritionBasis) {
  if (!nutritionBasis) {
    return '-';
  }
  const label = nutritionBasis.nutrient_label || nutritionBasis.nutrient_key || '영양성분';
  const value =
    nutritionBasis.nutrient_value === null || nutritionBasis.nutrient_value === undefined
      ? '-'
      : nutritionBasis.nutrient_value;
  return `${label} ${value}${nutritionBasis.unit || ''}`;
}

function formatLlmExplanationStatus(status) {
  const labels = {
    PENDING: '생성 대기',
    RUNNING: '생성 중',
    READY: '생성 완료',
    FALLBACK: '기본 설명 생성',
    FAILED: '생성 실패',
  };
  return labels[status] || status || '미요청';
}

function isLlmExplanationPending(status) {
  return ['PENDING', 'RUNNING'].includes(String(status || '').toUpperCase());
}

function firstSentence(text, fallback = '') {
  if (!text) {
    return fallback;
  }
  const normalized = String(text).replace(/\s+/g, ' ').trim();
  const match = normalized.match(/^(.+?[.!?。]|.+?다\.)\s/);
  return match ? match[1] : normalized;
}

function buildProductSummary(product) {
  const productName = product.product_name || product.sku_name || product.gs_product_id;
  const whyRecommended = String(product.why_recommended || '').replace(/\s+/g, ' ').trim();
  if (whyRecommended) {
    return whyRecommended;
  }
  return firstSentence(product.reason, `${productName}은 선택한 가족 건강목표와 연결된 추천 후보입니다.`);
}

function buildProductEvidence(product) {
  const basis = product.nutrition_basis || {};
  const evidence = [];
  const nutrition = formatNutritionValue(basis);
  if (nutrition !== '-') {
    evidence.push(`핵심 영양근거: ${nutrition}${basis.serving_basis ? ` / ${basis.serving_basis}` : ''}`);
  }
  if (basis.health_goal?.label || basis.health_goal?.code) {
    const patientCount = basis.health_goal.goal_patient_count || basis.health_goal.patient_count;
    evidence.push(
      `연결 건강목표: ${basis.health_goal.label || basis.health_goal.code}${
        patientCount ? ` (${patientCount}명)` : ''
      }`,
    );
  }
  if (Array.isArray(basis.product_health_tags) && basis.product_health_tags.length) {
    evidence.push(`상품 태그: ${basis.product_health_tags.slice(0, 4).join(', ')}`);
  }
  if (basis.mapping_level || basis.match_status) {
    evidence.push(`영양 매핑: ${basis.mapping_level || '-'} / ${basis.match_status || '-'}`);
  }
  return evidence.slice(0, 4);
}

function countBy(items, keyGetter) {
  return (items || []).reduce((acc, item) => {
    const key = keyGetter(item) || '-';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
}

function RetailIntelligenceConsolePage() {
  const [apiBase, setApiBase] = useState(readInitialApiBase);
  const [apiKey, setApiKey] = useState('');
  const [scopeUsers, setScopeUsers] = useState(FALLBACK_SCOPE_USERS);
  const [scopeLoadError, setScopeLoadError] = useState(null);
  const [form, setForm] = useState({
    service_mode: SERVICE_MODE_RETAIL_LINKED,
    user_no: FALLBACK_SCOPE_USERS[0].user_no,
    customer_member_no: '',
    patient_ids: selectedPatientIdsForUser(FALLBACK_SCOPE_USERS[0]),
    sales_channel: 'GS THE FRESH',
    availability_scope: 'pilot',
    max_products_per_goal: 3,
    shopping_context: 'ANY',
    conversational_request: '',
    store_type: 'GS_THE_FRESH',
    store_code: 'REPRESENTATIVE_STORE',
    inventory_check_required: true,
    mock_inventory: true,
  });
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [trace, setTrace] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState(null);
  const [llmExplanation, setLlmExplanation] = useState(null);
  const [llmExplanationLoading, setLlmExplanationLoading] = useState(false);
  const [llmExplanationError, setLlmExplanationError] = useState(null);
  const [error, setError] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const llmPollTimerRef = useRef(null);

  const selectedUser = useMemo(
    () => scopeUsers.find((user) => user.user_no === form.user_no) || scopeUsers[0],
    [form.user_no, scopeUsers],
  );

  useEffect(() => {
    const normalizedApiBase = normalizeBaseUrl(apiBase);
    if (!normalizedApiBase) {
      return undefined;
    }

    let ignore = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        const result = await fetchPatientScopes(normalizedApiBase, apiKey);
        if (ignore) {
          return;
        }
        const nextUsers = normalizeScopeUsers(result.users);
        if (!nextUsers.length) {
          return;
        }
        setScopeUsers(nextUsers);
        setScopeLoadError(null);
        setForm((prev) => {
          const nextUser = nextUsers.find((user) => user.user_no === prev.user_no) || nextUsers[0];
          const validPatientIds = new Set(nextUser.patients.map((patient) => patient.patient_id));
          const preservedPatientIds = prev.patient_ids.filter((patientId) => validPatientIds.has(patientId));
          return {
            ...prev,
            user_no: nextUser.user_no,
            patient_ids: preservedPatientIds.length ? preservedPatientIds : selectedPatientIdsForUser(nextUser),
          };
        });
      } catch (err) {
        if (!ignore) {
          setScopeLoadError(err.message || '사용자/환자 목록을 불러오지 못했습니다.');
        }
      }
    }, 250);

    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [apiBase, apiKey]);

  const requestPayload = useMemo(
    () => {
      const serviceMode = form.service_mode || SERVICE_MODE_RETAIL_LINKED;
      const basePayload = {
        service_mode: serviceMode,
        user_no: String(form.user_no).trim(),
        patient_ids: form.patient_ids,
        max_products_per_goal: Number(form.max_products_per_goal),
        shopping_context: form.shopping_context,
      };
      if (serviceMode === SERVICE_MODE_AIDRQ_STANDALONE) {
        return basePayload;
      }
      return {
        ...basePayload,
        customer_member_no: String(form.customer_member_no || '').trim() || undefined,
        sales_channel: form.sales_channel,
        availability_scope: String(form.availability_scope).trim(),
        store_type: String(form.store_type || '').trim() || undefined,
        store_code: String(form.store_code || '').trim() || undefined,
        inventory_check_required: Boolean(form.inventory_check_required),
        mock_inventory: Boolean(form.mock_inventory),
      };
    },
    [form],
  );

  const resultStats = useMemo(() => {
    const products = Array.isArray(response?.recommended_products) ? response.recommended_products : [];
    const cautionCount = products.filter((item) => item.caution_note).length;
    const sources = new Set(products.map((item) => item.nutrition_basis?.nutrition_source).filter(Boolean));
    return {
      productCount: products.length,
      cautionCount,
      sourceLabel: sources.size ? Array.from(sources).join(', ') : '-',
      safeResponse: response ? !hasForbiddenResponseFields(response) : null,
    };
  }, [response]);

  const traceStats = useMemo(() => {
    const excludedByReason = countBy(trace?.excluded_candidates, (item) => item.exclusion_reason);
    const ranked = Array.isArray(trace?.ranked_candidates) ? trace.ranked_candidates : [];
    const guardrails = Array.isArray(trace?.guardrail_checks) ? trace.guardrail_checks : [];
    return {
      wellnessTags: trace?.health_context?.wellness_tags || [],
      queryHints: trace?.health_context?.query_hints || [],
      profileTags: trace?.wellness_profile?.wellness_tags || [],
      ragCandidates: trace?.rag_candidates || [],
      excludedByReason,
      ranked,
      guardrails,
    };
  }, [trace]);

  const clearLlmExplanationPoll = useCallback(() => {
    if (llmPollTimerRef.current) {
      window.clearTimeout(llmPollTimerRef.current);
      llmPollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearLlmExplanationPoll(), [clearLlmExplanationPoll]);

  const scheduleLlmExplanationPoll = useCallback(
    (recommendationSetId, attempt = 0) => {
      clearLlmExplanationPoll();
      if (!recommendationSetId || attempt >= 18) {
        return;
      }
      const delayMs = attempt === 0 ? 1800 : 3500;
      llmPollTimerRef.current = window.setTimeout(async () => {
        try {
          const latest = await fetchLlmExplanation(apiBase, recommendationSetId, apiKey);
          setLlmExplanation(latest);
          if (isLlmExplanationPending(latest?.status)) {
            scheduleLlmExplanationPoll(recommendationSetId, attempt + 1);
          }
        } catch (err) {
          setLlmExplanationError(err.message || 'AI 설명 상태를 다시 확인하지 못했습니다.');
        }
      }, delayMs);
    },
    [apiBase, apiKey, clearLlmExplanationPoll],
  );

  const startLlmExplanationGeneration = useCallback(
    async (recommendationSetId, { forceRefresh = false, showLoading = false } = {}) => {
      if (!recommendationSetId) {
        return;
      }
      clearLlmExplanationPoll();
      if (showLoading) {
        setLlmExplanationLoading(true);
      }
      setLlmExplanationError(null);
      try {
        const result = await requestLlmExplanation(apiBase, recommendationSetId, apiKey, forceRefresh);
        setLlmExplanation(result);
        if (isLlmExplanationPending(result?.status)) {
          scheduleLlmExplanationPoll(recommendationSetId);
        }
      } catch (err) {
        setLlmExplanationError(err.message || 'AI 설명 생성을 시작하지 못했습니다.');
      } finally {
        if (showLoading) {
          setLlmExplanationLoading(false);
        }
      }
    },
    [apiBase, apiKey, clearLlmExplanationPoll, scheduleLlmExplanationPoll],
  );

  const refreshLlmExplanation = useCallback(
    async (recommendationSetId, { showLoading = true } = {}) => {
      if (!recommendationSetId) {
        return;
      }
      if (showLoading) {
        setLlmExplanationLoading(true);
      }
      setLlmExplanationError(null);
      try {
        const latest = await fetchLlmExplanation(apiBase, recommendationSetId, apiKey);
        setLlmExplanation(latest);
        if (latest?.available === false) {
          await startLlmExplanationGeneration(recommendationSetId, { showLoading: false });
          return;
        }
        if (isLlmExplanationPending(latest?.status)) {
          scheduleLlmExplanationPoll(recommendationSetId);
        }
      } catch (err) {
        setLlmExplanationError(err.message || 'AI 설명 상태를 확인하지 못했습니다.');
      } finally {
        if (showLoading) {
          setLlmExplanationLoading(false);
        }
      }
    },
    [apiBase, apiKey, scheduleLlmExplanationPoll, startLlmExplanationGeneration],
  );

  const updateForm = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleUserChange = useCallback(
    (userNo) => {
      clearLlmExplanationPoll();
      const nextUser = scopeUsers.find((user) => user.user_no === userNo);
      setForm((prev) => ({
        ...prev,
        user_no: userNo,
        patient_ids: selectedPatientIdsForUser(nextUser),
      }));
      setError(null);
      setResponse(null);
      setTrace(null);
      setTraceError(null);
      setLlmExplanation(null);
      setLlmExplanationError(null);
      setLlmExplanationLoading(false);
    },
    [clearLlmExplanationPoll, scopeUsers],
  );

  const togglePatient = useCallback(
    (patientId) => {
      clearLlmExplanationPoll();
      setForm((prev) => {
        const nextSelected = new Set(prev.patient_ids);
        if (nextSelected.has(patientId)) {
          nextSelected.delete(patientId);
        } else {
          nextSelected.add(patientId);
        }
        const orderedPatientIds = (selectedUser?.patients || [])
          .map((patient) => patient.patient_id)
          .filter((id) => nextSelected.has(id));
        return { ...prev, patient_ids: orderedPatientIds };
      });
      setError(null);
      setResponse(null);
      setTrace(null);
      setTraceError(null);
      setLlmExplanation(null);
      setLlmExplanationError(null);
      setLlmExplanationLoading(false);
    },
    [clearLlmExplanationPoll, selectedUser],
  );

  const selectAllPatients = useCallback(() => {
    clearLlmExplanationPoll();
    setForm((prev) => ({ ...prev, patient_ids: selectedPatientIdsForUser(selectedUser) }));
    setError(null);
    setResponse(null);
    setTrace(null);
    setTraceError(null);
    setLlmExplanation(null);
    setLlmExplanationError(null);
    setLlmExplanationLoading(false);
  }, [clearLlmExplanationPoll, selectedUser]);

  const clearPatients = useCallback(() => {
    clearLlmExplanationPoll();
    setForm((prev) => ({ ...prev, patient_ids: [] }));
    setError(null);
    setResponse(null);
    setTrace(null);
    setTraceError(null);
    setLlmExplanation(null);
    setLlmExplanationError(null);
    setLlmExplanationLoading(false);
  }, [clearLlmExplanationPoll]);

  const runSimulation = useCallback(async () => {
    clearLlmExplanationPoll();
    setLoading(true);
    setError(null);
    setResponse(null);
    setTrace(null);
    setTraceError(null);
    setLlmExplanation(null);
    setLlmExplanationError(null);
    setLlmExplanationLoading(false);
    const conversationalText = String(form.conversational_request || '').trim();
    const effectivePayload = conversationalText
      ? {
          base_request: requestPayload,
          user_request: {
            text: conversationalText,
          },
        }
      : requestPayload;
    setLastPayload(effectivePayload);
    rememberApiBase(apiBase);
    try {
      const result = conversationalText
        ? await postConversationalRecommendation(apiBase, effectivePayload, apiKey)
        : await postRecommendation(apiBase, requestPayload, apiKey);
      setResponse(result);
      if (result?.recommendation_set_id) {
        setLlmExplanation({
          status: 'PENDING',
          provider: 'VERTEX_AI_GEMINI',
          prompt_version: 'retail-narrative-prompt-v1',
          message: 'AI 설명을 준비하고 있습니다.',
        });
        startLlmExplanationGeneration(result.recommendation_set_id, { showLoading: false });
      }
      setTraceLoading(true);
      try {
        const traceResult = await fetchRecommendationTrace(apiBase, result.recommendation_set_id, apiKey);
        setTrace(traceResult);
      } catch (traceErr) {
        setTraceError(traceErr.message || '추천 처리 과정을 불러오지 못했습니다.');
      } finally {
        setTraceLoading(false);
      }
    } catch (err) {
      setError({
        status: err.status || '-',
        message: err.message || '추천 요청 실패',
        body: err.body || null,
      });
      setTraceLoading(false);
    } finally {
      setLoading(false);
    }
  }, [apiBase, apiKey, clearLlmExplanationPoll, form.conversational_request, requestPayload, startLlmExplanationGeneration]);

  const handleLlmExplanationView = useCallback(async () => {
    if (!response?.recommendation_set_id) {
      return;
    }
    await refreshLlmExplanation(response.recommendation_set_id, { showLoading: true });
  }, [refreshLlmExplanation, response]);

  const handleLlmExplanationRegenerate = useCallback(async () => {
    if (!response?.recommendation_set_id) {
      return;
    }
    await startLlmExplanationGeneration(response.recommendation_set_id, { forceRefresh: true, showLoading: true });
  }, [response, startLlmExplanationGeneration]);

  const allPatientsSelected =
    selectedUser?.patients?.length > 0 && selectedUser.patients.every((patient) => form.patient_ids.includes(patient.patient_id));
  const selectedServiceModeCopy = serviceModeCopy(form.service_mode);
  const responseServiceModeCopy = serviceModeCopy(response?.service_mode || form.service_mode);
  const standaloneMode = form.service_mode === SERVICE_MODE_AIDRQ_STANDALONE;

  return (
    <div className="retail-intel-page">
      <header className="retail-intel-header">
        <div>
          <p className="retail-intel-eyebrow">Retail Intelligence</p>
          <h1>리테일 추천 시뮬레이터</h1>
          <p>
            사용자와 가족, 실행 모드를 바꿔가며 구매 가능 SKU 추천과 건강 장보기 참고 추천의 응답, 추천 사유, 영양 근거,
            주의 문구를 확인합니다.
          </p>
        </div>
        <button type="button" onClick={runSimulation} disabled={loading || requestPayload.patient_ids.length === 0}>
          {loading ? '요청 중' : '추천 실행'}
        </button>
      </header>

      <section className="retail-intel-panel">
        <div className="retail-intel-panel-title">
          <h2>요청 설정</h2>
          <span>기본 API: {DEFAULT_API_BASE}</span>
        </div>
        <div className="retail-intel-form-grid">
          <label>
            추천 API 주소
            <input value={apiBase} onChange={(event) => setApiBase(event.target.value)} />
          </label>
          <label>
            API Key
            <input
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="인증 사용 시 입력"
            />
          </label>
          <label>
            사용자
            <select value={form.user_no} onChange={(event) => handleUserChange(event.target.value)}>
              {scopeUsers.map((user) => (
                <option key={user.user_no} value={user.user_no}>
                  {user.display_name} ({user.user_no})
                </option>
              ))}
            </select>
          </label>
          <label>
            추천 실행 모드
            <select value={form.service_mode} onChange={(event) => updateForm('service_mode', event.target.value)}>
              <option value={SERVICE_MODE_RETAIL_LINKED}>GS 연계 · 구매 가능 SKU 추천</option>
              <option value={SERVICE_MODE_AIDRQ_STANDALONE}>AI닥터큐 단독 · 건강 장보기 참고 추천</option>
            </select>
            <small className="retail-intel-field-help">{selectedServiceModeCopy.description}</small>
          </label>
          <label>
            목표별 추천 상품 수
            <input
              type="number"
              min="1"
              max="20"
              value={form.max_products_per_goal}
              onChange={(event) => updateForm('max_products_per_goal', event.target.value)}
            />
          </label>
          <label>
            장보기 목적
            <select value={form.shopping_context} onChange={(event) => updateForm('shopping_context', event.target.value)}>
              <option value="ANY">전체</option>
              <option value="BREAKFAST">아침</option>
              <option value="SNACK">간식</option>
              <option value="SIDE_DISH">반찬</option>
              <option value="DINNER">저녁</option>
            </select>
          </label>
          <label className="retail-intel-wide-field">
            자연어 요청
            <textarea
              value={form.conversational_request}
              onChange={(event) => updateForm('conversational_request', event.target.value)}
              rows={3}
              placeholder="예: 나 오늘 울적한데 기분 전환할 수 있는 상큼한 음식 뭐 없을까?"
            />
            <small className="retail-intel-field-help">
              입력하면 conversational recommendation endpoint를 호출하고, 비워두면 기존 추천 endpoint를 호출합니다.
            </small>
          </label>
          <label>
            GS 고객회원번호
            <input
              value={form.customer_member_no}
              onChange={(event) => updateForm('customer_member_no', event.target.value)}
              placeholder="GS 호출 연계 시 입력"
              disabled={standaloneMode}
            />
          </label>
          <label>
            점포유형
            <select value={form.store_type} onChange={(event) => updateForm('store_type', event.target.value)} disabled={standaloneMode}>
              <option value="GS_THE_FRESH">GS THE FRESH</option>
              <option value="GS25">GS25</option>
            </select>
          </label>
          <label>
            점포코드
            <input
              value={form.store_code}
              onChange={(event) => updateForm('store_code', event.target.value)}
              placeholder="대표매장 또는 실제 점포코드"
              disabled={standaloneMode}
            />
          </label>
          <label className="retail-intel-check-field">
            <input
              type="checkbox"
              checked={form.inventory_check_required}
              onChange={(event) => updateForm('inventory_check_required', event.target.checked)}
              disabled={standaloneMode}
            />
            재고 확인
          </label>
          <label className="retail-intel-check-field">
            <input
              type="checkbox"
              checked={form.mock_inventory}
              onChange={(event) => updateForm('mock_inventory', event.target.checked)}
              disabled={standaloneMode}
            />
            Mock 재고 사용
          </label>
          <div className="retail-intel-field retail-intel-patient-field">
            <span className="retail-intel-field-label">가족</span>
            <div className="retail-intel-patient-box">
              <div className="retail-intel-patient-actions">
                <span>{selectedUser?.display_name || '사용자'}의 가족</span>
                <div>
                  <button type="button" onClick={selectAllPatients} disabled={allPatientsSelected}>
                    전체 선택
                  </button>
                  <button type="button" onClick={clearPatients} disabled={form.patient_ids.length === 0}>
                    선택 해제
                  </button>
                </div>
              </div>
              <div className="retail-intel-patient-options">
                {(selectedUser?.patients || []).map((patient) => (
                  <label key={patient.patient_id} className="retail-intel-patient-option">
                    <input
                      type="checkbox"
                      checked={form.patient_ids.includes(patient.patient_id)}
                      onChange={() => togglePatient(patient.patient_id)}
                    />
                    <span>{patient.display_name}</span>
                    <small>건강목표 신호 {patient.wellness_signal_count}개</small>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <label>
            노출 채널
            <select value={form.sales_channel} onChange={(event) => updateForm('sales_channel', event.target.value)} disabled={standaloneMode}>
              <option value="GS THE FRESH">GS THE FRESH</option>
              <option value="우리동네GS">우리동네GS</option>
              <option value="UNKNOWN_CHANNEL">후보 없음 테스트</option>
            </select>
          </label>
          <label>
            파일럿 범위
            <input
              value={form.availability_scope}
              onChange={(event) => updateForm('availability_scope', event.target.value)}
              disabled={standaloneMode}
            />
          </label>
        </div>
        <p className="retail-intel-service-mode-note">
          현재 모드: <strong>{selectedServiceModeCopy.requestLabel}</strong>
        </p>
        {scopeLoadError ? <p className="retail-intel-inline-note">{scopeLoadError}</p> : null}
      </section>

      <div className="retail-intel-grid">
        <section className="retail-intel-panel">
          <div className="retail-intel-panel-title">
            <h2>요청 데이터</h2>
            <span>{lastPayload ? '마지막 전송' : '현재 입력'}</span>
          </div>
          <pre>{formatJson(lastPayload || requestPayload)}</pre>
        </section>

        <section className="retail-intel-panel">
          <div className="retail-intel-panel-title">
            <h2>검증 체크</h2>
            <span>{response ? '응답 수신' : '대기'}</span>
          </div>
          <dl className="retail-intel-checks">
            <div>
              <dt>추천 상품 수</dt>
              <dd>{resultStats.productCount}</dd>
            </div>
            <div>
              <dt>주의 문구 포함</dt>
              <dd>{resultStats.cautionCount}</dd>
            </div>
            <div>
              <dt>영양 출처</dt>
              <dd>{resultStats.sourceLabel}</dd>
            </div>
            <div>
              <dt>실행 모드</dt>
              <dd>{response ? responseServiceModeCopy.requestLabel : selectedServiceModeCopy.requestLabel}</dd>
            </div>
            <div>
              <dt>응답 제외 필드</dt>
              <dd className={resultStats.safeResponse === false ? 'is-bad' : 'is-good'}>
                {resultStats.safeResponse === null ? '-' : resultStats.safeResponse ? '미포함' : '확인 필요'}
              </dd>
            </div>
          </dl>
        </section>
      </div>

      {error ? (
        <section className="retail-intel-panel retail-intel-error">
          <div className="retail-intel-panel-title">
            <h2>오류 응답</h2>
            <span>HTTP {error.status}</span>
          </div>
          <p>{error.message}</p>
          {error.body ? <pre>{formatJson(error.body)}</pre> : null}
        </section>
      ) : null}

      {response ? (
        <section className="retail-intel-panel">
          <div className="retail-intel-panel-title">
            <h2>추천 결과</h2>
            <span>{formatDateTime(response.generated_at)}</span>
          </div>
          <div className="retail-intel-summary">
            <div>
              <span>추천 세트 ID</span>
              <strong>{response.recommendation_set_id}</strong>
            </div>
            <div>
              <span>건강목표</span>
              <strong>
                {response.health_goal?.label} ({response.health_goal?.code})
              </strong>
              {response.supporting_health_goals?.length ? (
                <div className="retail-intel-supporting-goals">
                  {response.supporting_health_goals.slice(0, 4).map((goal) => (
                    <span key={goal.code}>{goal.label || goal.code}</span>
                  ))}
                </div>
              ) : null}
            </div>
            <div>
              <span>추천 모드</span>
              <strong>{formatRecommendationMode(response.recommendation_mode)}</strong>
            </div>
            <div>
              <span>실행 모드</span>
              <strong>{responseServiceModeCopy.resultLabel}</strong>
            </div>
            <div>
              <span>정책 버전</span>
              <strong>{response.policy_version}</strong>
            </div>
          </div>

          <div className="retail-intel-process">
            <div className="retail-intel-panel-title">
              <h2>2단계 처리 과정</h2>
              <span>{traceLoading ? '조회 중' : trace?.available ? 'Trace 연결' : traceError || trace?.message || 'Trace 없음'}</span>
            </div>
            <div className="retail-intel-stage-grid">
              <article className="retail-intel-stage-card">
                <span className="retail-intel-stage-index">1</span>
                <h3>건강 컨텍스트</h3>
                <p>
                  대상 {trace?.health_context?.patient_count ?? form.patient_ids.length}명 / 원문 포함{' '}
                  {trace?.health_context?.raw_health_payload_included ? '있음' : '없음'}
                </p>
                <div className="retail-intel-chip-row">
                  {traceStats.wellnessTags.slice(0, 6).map((tag) => (
                    <span key={`${formatTraceTag(tag)}-${tag.patient_count || 0}`}>
                      {formatTraceTag(tag)} · {formatTraceNumber(tag.patient_count)}명 · 우선 {formatTraceNumber(tag.priority_score)}
                    </span>
                  ))}
                  {!traceStats.wellnessTags.length ? <span>대기</span> : null}
                </div>
                <ol className="retail-intel-debug-list">
                  {traceStats.queryHints.slice(0, 2).map((hint) => (
                    <li key={`${hint.tag_code}-${hint.query_text}`}>
                      {hint.tag_label || hint.tag_code}
                      <small>{hint.query_text}</small>
                    </li>
                  ))}
                  {!traceStats.queryHints.length ? <li>검색 힌트 대기</li> : null}
                </ol>
              </article>

              <article className="retail-intel-stage-card">
                <span className="retail-intel-stage-index">2</span>
                <h3>웰니스 프로파일</h3>
                <p>
                  {trace?.wellness_profile?.profile_source || 'rule/LLM'} · 신뢰도{' '}
                  {formatTraceNumber(trace?.wellness_profile?.confidence)}
                </p>
                <p>
                  fallback {String(trace?.wellness_profile?.fallback_used ?? '-')} · 오류{' '}
                  {trace?.wellness_profile?.error_code || '없음'}
                </p>
                <p className="retail-intel-mode-line">
                  {formatRecommendationMode(trace?.wellness_profile?.recommendation_mode || response.recommendation_mode)}
                </p>
                <div className="retail-intel-chip-row">
                  {traceStats.profileTags.slice(0, 6).map((tag) => (
                    <span key={`${formatTraceTag(tag)}-${tag.priority || 0}`}>
                      {formatTraceTag(tag)} · {formatTraceNumber(tag.priority)} · {formatRationaleCode(tag.rationale_code)}
                    </span>
                  ))}
                  {!traceStats.profileTags.length ? <span>대기</span> : null}
                </div>
                <ol className="retail-intel-debug-list">
                  {traceStats.profileTags.slice(0, 3).map((tag) => (
                    <li key={`${formatTraceTag(tag)}-source`}>
                      원천 {formatTraceTag(tag)}
                      <small>{formatTraceSources(tag.source_tag_codes)}</small>
                    </li>
                  ))}
                </ol>
              </article>

              <article className="retail-intel-stage-card">
                <span className="retail-intel-stage-index">3</span>
                <h3>RAG 후보</h3>
                <p>후보 {traceStats.ragCandidates.length}건 / 최종 확정 전 보조 검색</p>
                <ol className="retail-intel-compact-list">
                  {traceStats.ragCandidates.slice(0, 3).map((candidate) => (
                    <li key={candidate.document_key || `${candidate.rank}-${candidate.title}`}>
                      {candidate.title} <small>{candidate.kb_layer} · {formatTraceNumber(candidate.score)}</small>
                    </li>
                  ))}
                  {!traceStats.ragCandidates.length ? <li>검색 후보 없음</li> : null}
                </ol>
              </article>

              <article className="retail-intel-stage-card">
                <span className="retail-intel-stage-index">4</span>
                <h3>검증/제외</h3>
                <p>
                  포함 {trace?.stage_counts?.candidate_included || 0}건 / 제외{' '}
                  {trace?.stage_counts?.candidate_excluded || 0}건
                </p>
                <div className="retail-intel-chip-row">
                  {Object.entries(traceStats.excludedByReason).slice(0, 5).map(([reason, count]) => (
                    <span key={reason}>{reason} · {count}</span>
                  ))}
                  {!Object.keys(traceStats.excludedByReason).length ? <span>제외 없음</span> : null}
                </div>
              </article>

              <article className="retail-intel-stage-card">
                <span className="retail-intel-stage-index">5</span>
                <h3>최종 랭킹/설명</h3>
                <p>
                  랭킹 {traceStats.ranked.length}건 / 안전검사 {traceStats.guardrails.length}건
                </p>
                <ol className="retail-intel-compact-list">
                  {traceStats.ranked.slice(0, 3).map((item) => (
                    <li key={`${item.gs_product_id}-${item.payload?.rank || item.id}`}>
                      #{item.payload?.rank || '-'} {item.gs_product_id}
                      <small>
                        {item.payload?.core_nutrient_key || '-'} · {formatTraceNumber(item.payload?.core_nutrient_value)}
                        {' / 목적 '}
                        {formatTraceNumber(item.payload?.hybrid_score_breakdown?.shopping_context_adjustment)}
                      </small>
                    </li>
                  ))}
                  {!traceStats.ranked.length ? <li>랭킹 trace 없음</li> : null}
                </ol>
              </article>
            </div>
          </div>

          <div className="retail-intel-products">
            {(response.recommended_products || []).map((product) => (
              <article key={`${product.rank}-${product.gs_product_id}`} className="retail-intel-product">
                <div className="retail-intel-product-rank">#{product.rank}</div>
                <div className="retail-intel-product-body">
                  <h3>{product.product_name || product.sku_name || product.gs_product_id}</h3>
                  <div className="retail-intel-product-meta">
                    <span>{product.category || '카테고리 미지정'}</span>
                    <span>
                      {responseServiceModeCopy.productIdLabel} {product.gs_product_id}
                    </span>
                    {product.sku_id ? <span>SKU {product.sku_id}</span> : null}
                  </div>
                  <p className="retail-intel-product-summary">{buildProductSummary(product)}</p>
                  <ul className="retail-intel-product-evidence">
                    {buildProductEvidence(product).map((item) => (
                      <li key={`${product.rank}-${item}`}>{item}</li>
                    ))}
                  </ul>
                  {product.caution_note ? (
                    <details className="retail-intel-product-details retail-intel-caution-details">
                      <summary>주의사항 보기</summary>
                      <p>{product.caution_note}</p>
                    </details>
                  ) : null}
                  <details className="retail-intel-product-details">
                    <summary>상세 근거 보기</summary>
                    <table>
                      <tbody>
                        <tr>
                          <th>추천 이유 원문</th>
                          <td>{product.reason || '-'}</td>
                        </tr>
                        <tr>
                          <th>추천 요약</th>
                          <td>{product.why_recommended || '-'}</td>
                        </tr>
                        <tr>
                          <th>핵심 영양성분</th>
                          <td>{formatNutritionValue(product.nutrition_basis)}</td>
                        </tr>
                        <tr>
                          <th>기준 단위</th>
                          <td>{product.nutrition_basis?.serving_basis || '-'}</td>
                        </tr>
                        <tr>
                          <th>매핑 상태</th>
                          <td>
                            {product.nutrition_basis?.mapping_level || '-'} / {product.nutrition_basis?.match_status || '-'}
                          </td>
                        </tr>
                        <tr>
                          <th>출처</th>
                          <td>
                            {product.nutrition_basis?.nutrition_source || '-'} / 신뢰도{' '}
                            {product.nutrition_basis?.match_confidence ?? '-'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </details>
                </div>
              </article>
            ))}
          </div>

          {Array.isArray(response.today_meal_cards) && response.today_meal_cards.length ? (
            <div className="retail-intel-meal-cards">
              <div className="retail-intel-section-heading">
                <h3>오늘 이렇게 먹기</h3>
                <span>검증/검토 메뉴 후보</span>
              </div>
              {response.today_meal_cards.map((card) => (
                <article key={card.menu_id} className="retail-intel-meal-card">
                  <div>
                    <div className="retail-intel-meal-card-title">
                      <h4>{card.title}</h4>
                      <span>{card.verified ? '검증됨' : card.review_status || '검토 필요'}</span>
                    </div>
                    {card.subtitle ? <p className="retail-intel-meal-subtitle">{card.subtitle}</p> : null}
                    <p>{formatMealCardSummary(card)}</p>
                    {card.ingredients_text || (card.recipe_steps || []).length ? (
                      <details className="retail-intel-meal-recipe">
                        <summary>재료와 만드는 법</summary>
                        {card.ingredients_text ? (
                          <div>
                            <strong>재료</strong>
                            <p>{card.ingredients_text}</p>
                          </div>
                        ) : null}
                        {(card.recipe_steps || []).length ? (
                          <ol>
                            {(card.recipe_steps || []).map((step, index) => (
                              <li key={`${card.menu_id}-step-${index}`}>{step.replace(/^\d+[.)]?\s*/, '')}</li>
                            ))}
                          </ol>
                        ) : null}
                      </details>
                    ) : null}
                    {cleanMealUsageTip(card.usage_tip) ? (
                      <p className="retail-intel-meal-tip">{cleanMealUsageTip(card.usage_tip)}</p>
                    ) : null}
                    <div className="retail-intel-product-meta">
                      {(card.meal_context_scope || []).map((context) => (
                        <span key={`${card.menu_id}-${context}`}>{context}</span>
                      ))}
                      {(card.wellness_tags || []).slice(0, 4).map((tag) => (
                        <span key={`${card.menu_id}-${tag}`}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <dl className="retail-intel-meal-detail">
                    <div>
                      <dt>연결 상품</dt>
                      <dd>{(card.matched_product_names || []).join(', ') || '-'}</dd>
                    </div>
                    <div>
                      <dt>핵심 재료군</dt>
                      <dd>{(card.required_product_groups || []).join(', ') || '-'}</dd>
                    </div>
                    <div>
                      <dt>영양 요약</dt>
                      <dd>{card.nutrition_summary || '-'}</dd>
                    </div>
                    <div>
                      <dt>출처</dt>
                      <dd>
                        {formatMealSourceUrl(card) ? (
                          <a href={formatMealSourceUrl(card)} target="_blank" rel="noreferrer">
                            {formatMealSourceLabel(card)}
                          </a>
                        ) : (
                          formatMealSourceLabel(card)
                        )}
                      </dd>
                    </div>
                  </dl>
                  {card.caution_note ? <p className="retail-intel-meal-caution">{card.caution_note}</p> : null}
                </article>
              ))}
            </div>
          ) : null}

          <div className="retail-intel-ai-explanation">
            <div className="retail-intel-section-heading retail-intel-ai-heading">
              <div>
                <h3>AI 가족 맞춤 설명</h3>
                <span>추천 상품과 메뉴를 연결한 가족용 요약</span>
              </div>
              <div className="retail-intel-ai-actions">
                <button
                  type="button"
                  onClick={handleLlmExplanationView}
                  disabled={llmExplanationLoading || !response.recommendation_set_id}
                >
                  {llmExplanationLoading ? '확인 중' : 'AI 설명 보기'}
                </button>
                <button
                  type="button"
                  onClick={handleLlmExplanationRegenerate}
                  disabled={llmExplanationLoading || !response.recommendation_set_id}
                >
                  다시 생성
                </button>
              </div>
            </div>
            {llmExplanationError ? <p className="retail-intel-ai-error">{llmExplanationError}</p> : null}
            {!llmExplanation ? (
              <p className="retail-intel-ai-empty">
                추천 결과를 바탕으로 AI 설명을 준비하고 있습니다. 잠시 후 AI 설명 보기를 눌러 확인하세요.
              </p>
            ) : (
              <div className="retail-intel-ai-card">
                <div className="retail-intel-ai-status-row">
                  <span className={`retail-intel-ai-status is-${String(llmExplanation.status || 'pending').toLowerCase()}`}>
                    {formatLlmExplanationStatus(llmExplanation.status)}
                  </span>
                  {llmExplanation.updated_at ? <span>{formatDateTime(llmExplanation.updated_at)}</span> : null}
                </div>
                {llmExplanation.available === false ? (
                  <p className="retail-intel-ai-empty">{llmExplanation.message || '설명 캐시를 사용할 수 없습니다.'}</p>
                ) : null}
                {llmExplanation.narrative?.summary ? (
                  <div className="retail-intel-ai-narrative">
                    {llmExplanation.narrative.headline ? <h4>{llmExplanation.narrative.headline}</h4> : null}
                    <p>{llmExplanation.narrative.summary}</p>
                    {llmExplanation.narrative.why_now ? <p>{llmExplanation.narrative.why_now}</p> : null}
                  </div>
                ) : null}
                {llmExplanation.narrative?.safety_note ? (
                  <p className="retail-intel-ai-safety">{llmExplanation.narrative.safety_note}</p>
                ) : null}
              </div>
            )}
          </div>
        </section>
      ) : null}

      <section className="retail-intel-panel">
        <div className="retail-intel-panel-title">
          <h2>원본 응답</h2>
          <span>{response ? 'JSON' : '응답 없음'}</span>
        </div>
        <pre>{response ? formatJson(response) : '추천 실행 후 응답이 표시됩니다.'}</pre>
      </section>
    </div>
  );
}

export default RetailIntelligenceConsolePage;
